import { AuthStorage } from "utils/auth-storage";
import {
  Button,
  Cascader,
  DatePicker,
  Empty,
  List,
  Modal,
  Skeleton,
  Tabs,
  Tooltip,
} from "antd";
import LoadingComponent from "components/loading-component";
import React, { useEffect, useMemo, useState } from "react";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import { Block } from "@material-ui/icons";
import { CheckOutlined } from "@ant-design/icons";
import { WarehouseServices } from "apis/Services/WarehouseService";
import toast from "react-hot-toast";
import moment from "moment";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { EditRow, reloadProjects } from "redux/project-reducer";
import { ProjectServices } from "apis/Services/ProjectService";
import styles from "./ItemsShippedModal.module.css";

dayjs.extend(customParseFormat);

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const STATUS_META = {
  Pending: {
    Icon: RestoreIcon,
    iconClass: styles.statusIconPending,
    pillClass: styles.statusPillPending,
  },
  "In Transit": {
    Icon: LocalShippingOutlinedIcon,
    iconClass: styles.statusIconTransit,
    pillClass: styles.statusPillTransit,
  },
  Transit: {
    Icon: LocalShippingOutlinedIcon,
    iconClass: styles.statusIconTransit,
    pillClass: styles.statusPillTransit,
  },
  Delivered: {
    Icon: Inventory2OutlinedIcon,
    iconClass: styles.statusIconDelivered,
    pillClass: styles.statusPillDelivered,
  },
  Cancelled: {
    Icon: Block,
    iconClass: styles.statusIconCancelled,
    pillClass: styles.statusPillCancelled,
  },
};

function getStatusMeta(status) {
  return STATUS_META[status] || STATUS_META.Pending;
}

function formatCurrency(value) {
  return currencyFormatter.format(Number(value || 0));
}

// Renders the modal used to review shipped items and transfer history.
function ItemsShippedModal({
  itemsShippedData,
  itemsShippedModal,
  itemsShippedHistoryData,
  isitemsShippedLoading,
  transfersToProjectInventoryHistory,
  setItemsShippedModal,
  projectID,
  accessLevel,
  projectDetails,
  editedItems,
  onTransferUpdated,
}) {
  const [projectAddressMap, setProjectAddressMap] = useState({});
  const [selectedEntryID, setSelectedEntryID] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedSubDivision, setSelectedSubDivision] = useState(null);
  const [selectedActualDeliveryDate, setSelectedActualDeliveryDate] = useState(
    dayjs().format("MM-DD-YYYY"),
  );
  const dispatch = useDispatch();

  // Refreshes transfer data and local form state whenever the modal opens.
  useEffect(() => {
    if (itemsShippedModal) {
      setSelectedEntryID(null);
      setSelectedDivision(null);
      setSelectedSubDivision(null);
      setSelectedActualDeliveryDate(dayjs().format("MM-DD-YYYY"));
      transfersToProjectInventoryHistory();
      getProjectsAddress();
    }
  }, [itemsShippedModal, transfersToProjectInventoryHistory]);

  // Loads job site addresses used in the shipped-items modal.
  function getProjectsAddress() {
    let company_id = AuthStorage.getItem("company_id");
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company: company_id,
      code,
      loginEmail: email,
    };
    WarehouseServices.getJobSites(ReqObj)
      .then((res) => {
        if (res) {
          const map = res.reduce((acc, item) => {
            acc[item.name] = item.address;
            return acc;
          }, {});

          setProjectAddressMap(map);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  // Closes the shipped-items modal.
  const onCancel = () => {
    setItemsShippedModal(false);
  };

  // Captures the selected division, subdivision, and entry from the cascader.
  const handleChange = (value) => {
    const divisionName = value?.[0] || null;
    const subdivisionName = value?.[1] || null;
    const entryID = value?.[value.length - 1] || null;

    setSelectedDivision(divisionName);
    setSelectedSubDivision(subdivisionName);
    setSelectedEntryID(entryID);
  };

  const options = useMemo(
    () =>
      Object.entries(projectDetails?.content || {}).map(
        ([divisionName, subdivisions]) => ({
          value: divisionName,
          label: (
            <Tooltip title={divisionName} placement="right">
              {divisionName}
            </Tooltip>
          ),
          searchValue: divisionName,
          children: Object.entries(subdivisions).map(
            ([subdivisionName, items]) => ({
              value: subdivisionName,
              label: (
                <Tooltip title={subdivisionName} placement="right">
                  {subdivisionName}
                </Tooltip>
              ),
              searchValue: subdivisionName,
              children: items.map((item) => {
                const labelText = item.specTitle || item.specNumber || "";

                return {
                  value: item.entryID,
                  label: (
                    <Tooltip title={labelText} placement="right">
                      {labelText}
                    </Tooltip>
                  ),
                  searchValue: labelText,
                };
              }),
            }),
          ),
        }),
      ),
    [projectDetails],
  );

  // Filters cascader options by any label in the selected path.
  const filter = (inputValue, path) => {
    return path.some((option) =>
      option.searchValue?.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  // Shows only the deepest selected cascader label in the input.
  const displayRender = (labels) => labels[labels.length - 1];

  const nonWorkingDays = projectDetails?.nonWorkingDays || {};

  // Disables weekends, holidays, and restricted future dates in the modal date pickers.
  const isDateDisabled = (
    date,
    isContractIssuedReleaseOrActualDeliveryDate,
  ) => {
    const disabledDatesArray = [];
    Object.values(nonWorkingDays).forEach((holiday) => {
      let currDate = moment(holiday.startDate, "MM/DD/YYYY").startOf("day");
      let lastDate = moment(holiday.finishDate, "MM/DD/YYYY").startOf("day");
      disabledDatesArray.push(moment(currDate).format("YYYY-MM-DD"));
      while (currDate.add(1, "days").diff(lastDate) < 0) {
        disabledDatesArray.push(moment(currDate).format("YYYY-MM-DD"));
      }
      disabledDatesArray.push(moment(lastDate).format("YYYY-MM-DD"));
    });

    const isWeekend = (dateToCheck) => [0, 6].includes(dateToCheck.day());
    const isFutureDate = (dateToCheck) => dateToCheck.isAfter(moment(), "day");

    if (isContractIssuedReleaseOrActualDeliveryDate) {
      return (
        date &&
        (disabledDatesArray.includes(date.format("YYYY-MM-DD")) ||
          isWeekend(date) ||
          isFutureDate(date))
      );
    }

    return (
      date &&
      (disabledDatesArray.includes(date.format("YYYY-MM-DD")) ||
        isWeekend(date))
    );
  };

  const canUpdate = (transfer) =>
    transfer.currentStatus === "Pending" ||
    transfer.currentStatus === "In Transit";

  const canCancel = (transfer) =>
    transfer.currentStatus !== "Delivered" &&
    transfer.currentStatus !== "Cancelled";

  // Pushes the selected shipped item back into the procurement log.
  const updateProcLog = () => {
    if (selectedEntryID) {
      const original = projectDetails.content[selectedDivision][
        selectedSubDivision
      ].find((item) => item.entryID === selectedEntryID);

      if (!original) return;

      const data = [
        {
          ...original,
          ActualDeliveryDate: dayjs(
            selectedActualDeliveryDate,
            "MM-DD-YYYY",
          ).format("MM-DD-YYYY"),
        },
      ];

      let company_id = AuthStorage.getItem("company_id");
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");

      let authData = {
        company_id,
        project_id: projectID,
        code,
        loginEmail: email,
      };

      ProjectServices.EditRow(data, authData)
        .then(() => {
          editedItems.forEach((item) => dispatch(EditRow(item)));
          dispatch(reloadProjects());
          onTransferUpdated?.();
        })
        .catch((err) => {
          console.log(err);
          toast.error("Something went wrong, please try again.");
        });
    }
  };

  // Cancels a pending transfer from the shipped-items modal.
  const handleCancelTransfer = (transfer) => {
    if (
      transfer.currentStatus !== "Delivered" &&
      transfer.cancelledStatus === ""
    ) {
      let company_id = AuthStorage.getItem("company_id");
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");
      let ReqObj = {
        company: company_id,
        project: projectID,
        code,
        loginEmail: email,
        itemID: transfer.transferID,
        status: "Cancelled",
      };
      WarehouseServices.updateTransferInventoryItem(ReqObj)
        .then(() => {
          toast.success("Status updated successfully");
          transfersToProjectInventoryHistory();
        })
        .catch((err) => {
          console.log(err);
          toast.error("Something went wrong, please try again.");
        });
    }
  };

  // Applies transfer updates and refreshes the shipped-items data.
  const handleUpdateTransfer = (transfer) => {
    if (
      transfer.currentStatus !== "Delivered" &&
      transfer.cancelledStatus === ""
    ) {
      let company_id = AuthStorage.getItem("company_id");
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");
      let ReqObj = {
        company: company_id,
        project: projectID,
        code,
        loginEmail: email,
        itemID: transfer.transferID,
        status: transfer.currentStatus === "Pending" ? "Transit" : "Delivered",
      };
      WarehouseServices.updateTransferInventoryItem(ReqObj)
        .then(() => {
          toast.success("Status updated successfully");
          updateProcLog();
          transfersToProjectInventoryHistory();
        })
        .catch((err) => {
          console.log(err);
          toast.error("Something went wrong, please try again.");
        });
    }
  };

  const renderFinalState = (transfer) => {
    if (transfer.currentStatus === "Delivered") {
      return (
        <div className={`${styles.finalState} ${styles.finalDelivered}`}>
          <CheckOutlined />
          <span>Delivered</span>
        </div>
      );
    }

    if (transfer.currentStatus === "Cancelled") {
      return (
        <div className={`${styles.finalState} ${styles.finalCancelled}`}>
          <Block fontSize="small" />
          <span>Cancelled</span>
        </div>
      );
    }

    return null;
  };

  const renderTransferItems = (transfer) => (
    <div className={styles.itemStack}>
      {(transfer.items || []).map((item, index) => (
        <div key={`${transfer.transferID}-${index}`} className={styles.itemRow}>
          <div className={styles.itemMeta}>
            <Inventory2OutlinedIcon className={styles.itemIcon} />
            <div className={styles.itemTextBlock}>
              <span className={styles.itemTitle}>{item.materialName}</span>
              <span className={styles.itemSubText}>SKU: {item.sku}</span>
              {item?.itemVendor ? (
                <span className={styles.itemSubText}>
                  Vendor: {item.itemVendor}
                </span>
              ) : null}
              <span className={styles.itemSubText}>{item.category || ""}</span>
              <span className={styles.itemSubText}>
                {item.subcategory || ""}
              </span>
            </div>
          </div>

          <div className={styles.itemMeta}>
            <LocationOnOutlinedIcon className={styles.itemIcon} />
            <div className={styles.itemTextBlock}>
              <span className={styles.itemTitle}>{transfer.toLocation}</span>
              <span className={styles.itemSubText}>
                {Object.keys(projectAddressMap).length > 0 ? (
                  projectAddressMap[transfer.toLocation] || "No address provided"
                ) : (
                  <Skeleton.Input
                    style={{ width: 120, height: 12 }}
                    active
                    size="small"
                  />
                )}
              </span>
              <span className={styles.itemSubText}>
                {transfer.toSubLocation}
              </span>
            </div>
          </div>

          <div className={styles.metricBlock}>
            <span className={styles.metricLabel}>Quantity</span>
            <span className={styles.metricValue}>
              {item.quantity} {item.quantity > 1 ? "units" : "unit"}
            </span>
          </div>

          <div className={styles.metricBlock}>
            <span className={styles.metricLabel}>Amount</span>
            <span className={`${styles.metricValue} ${styles.metricValueMuted}`}>
              {formatCurrency(item.unitPrice * item.quantity)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTransferCard = (transfer, showProcurementLinking) => {
    const statusMeta = getStatusMeta(transfer.currentStatus);
    const StatusIcon = statusMeta.Icon;
    const totalAmount = (transfer.items || []).reduce(
      (sum, item) => sum + Number(item.unitPrice || 0) * Number(item.quantity || 0),
      0,
    );

    return (
      <div className={styles.transferCard}>
        <div className={styles.transferHeader}>
          <div className={styles.transferIdentity}>
            <div
              className={`${styles.statusIconBadge} ${statusMeta.iconClass}`}
            >
              <StatusIcon fontSize="small" />
            </div>

            <div className={styles.transferIdentityText}>
              <span className={styles.transferNumber}>
                Transfer #{transfer.transferID}
              </span>
              <span className={`${styles.statusPill} ${statusMeta.pillClass}`}>
                {transfer.currentStatus}
              </span>
            </div>
          </div>

          <div className={styles.transferSummary}>
            <span className={styles.transferTotal}>
              {formatCurrency(totalAmount)}
            </span>
            <span className={styles.transferDate}>{transfer.createdAt}</span>
          </div>
        </div>

        {renderTransferItems(transfer)}

        {transfer.note ? (
          <div className={styles.notePanel}>
            <span className={styles.noteLabel}>Notes</span>
            <span className={styles.noteText}>{transfer.note}</span>
          </div>
        ) : null}

        <div className={styles.transferFooter}>
          {showProcurementLinking ? (
            <div className={styles.linkPanel}>
              <div className={styles.fieldGrid}>
                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>Pick a submittal</span>
                  <Cascader
                    className={styles.pillCascader}
                    options={options}
                    displayRender={displayRender}
                    onChange={handleChange}
                    popupMenuColumnStyle={{ maxWidth: 350 }}
                    placeholder="Select a submittal"
                    showSearch={{ filter }}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>
                    Actual Delivery Date
                  </span>
                  <DatePicker
                    className={styles.pillDatePicker}
                    name="actualDeliveryDate"
                    format="MM-DD-YYYY"
                    value={
                      selectedActualDeliveryDate
                        ? dayjs(selectedActualDeliveryDate, "MM-DD-YYYY")
                        : null
                    }
                    onChange={(date, dateString) => {
                      if (dateString) {
                        setSelectedActualDeliveryDate(
                          dayjs(dateString, "MM-DD-YYYY").format("MM-DD-YYYY"),
                        );
                      } else {
                        setSelectedActualDeliveryDate("");
                      }
                    }}
                    placeholder="Select date"
                    disabledDate={(date) => isDateDisabled(date, true)}
                    allowClear={false}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div />
          )}

          <div className={styles.actionPanel}>
            {canCancel(transfer) && canUpdate(transfer) ? (
              <div className={styles.actionButtons}>
                <Button
                  className={styles.secondaryButton}
                  disabled={accessLevel < 2}
                  onClick={() => handleCancelTransfer(transfer)}
                >
                  Cancel Transfer
                </Button>
                <Button
                  className={styles.primaryButton}
                  disabled={accessLevel < 2}
                  onClick={() => handleUpdateTransfer(transfer)}
                >
                  {transfer.currentStatus === "Pending"
                    ? "Ship"
                    : "Mark Delivered"}
                </Button>
              </div>
            ) : (
              renderFinalState(transfer)
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTransferList = (records, showProcurementLinking = false) => (
    <div className={styles.listPanel}>
      <List
        split={false}
        className={styles.transferList}
        dataSource={records || []}
        loading={isitemsShippedLoading}
        locale={{
          emptyText: (
            <Empty
              className={styles.emptyState}
              description="No warehouse transfers found."
            />
          ),
        }}
        renderItem={(transfer) => (
          <List.Item className={styles.listItem}>
            {renderTransferCard(transfer, showProcurementLinking)}
          </List.Item>
        )}
      />
    </div>
  );

  const tabItems = [
    projectDetails && {
      key: "1",
      label: "Pending Transfers",
      children: renderTransferList(itemsShippedData, true),
    },
    {
      key: "2",
      label: "History",
      children: renderTransferList(itemsShippedHistoryData, false),
    },
  ].filter(Boolean);

  return (
    <Modal
      open={itemsShippedModal}
      onCancel={onCancel}
      footer={null}
      width={940}
      centered
      className={styles.modal}
      title={
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderIcon}>
            <LocalShippingOutlinedIcon fontSize="small" />
          </div>
          <div>
            <div className={styles.modalHeaderTitle}>Warehouse Transfers</div>
            <div className={styles.modalHeaderSub}>
              Review active transfers, connect deliveries to submittals, and
              keep procurement history in sync.
            </div>
          </div>
        </div>
      }
    >
      {isitemsShippedLoading ? (
        <div className={styles.loadingState}>
          <LoadingComponent />
        </div>
      ) : (
        <Tabs defaultActiveKey="1" items={tabItems} className={styles.tabs} />
      )}
    </Modal>
  );
}

export default ItemsShippedModal;
